/* CaptureComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./CaptureRenderer");
const _ = require("lodash");
const MAX_PAGE_SIZE = 200;

/**
 * class CaptureComponent
 *
 * @fires CaptureComponent#DataUpdated
 */
class CaptureComponent extends EventEmitter {
    /**
     *
     * @param {CaptureEntity.} CaptureEntity
     * @param {UserSession} userSession
     * @param {UiUtils} uiUtils
     * @param {CoreLogger} logger
     */
    constructor(
        CommandSet,
        userSession,
        uiUtils,
        logger,
        userFeedback,
        DeviceEntity,
        RedactionRegexEntity,
        ConstructInstance,
        AllowedCommandsEntity,
        ValueMap,
        OrganizationService,
        steveIo,
        NetworkDiscoveryExclusionsEntity,
        SiteEntity,
        CustomFields
    ) {
        super();
        this._maxPageSize = MAX_PAGE_SIZE;
        this._CommandSetEntityDef = CommandSet;
        this._userSession = userSession;
        this._authorizedActions = _.get(this._userSession, "authorizedActions", []);
        this._logger = logger;
        this._muf = userFeedback;
        this._deviceEntityDef = DeviceEntity;
        this._redactionRegexEntityDef = RedactionRegexEntity;
        this._constructInstance = ConstructInstance;
        this._allowedCommandsEntityDef = AllowedCommandsEntity;
        this._siteEntity = SiteEntity;
        this._customFieldEntityClass = CustomFields;
        this._slickAllowedData = [];
        this._valueMapEntityDef = ValueMap;
        this._organizationService = OrganizationService;
        this._slickCommandData = [];
        this._commandSetsFeed = {};
        this._showStandardCommands = false;
        this._showDisabledCommands = false;
        this._NetworkDiscoveryExclusionsEntity = NetworkDiscoveryExclusionsEntity;
        this._networkDiscoveryExclusionEntity = undefined;
        this._typeBaseMap = [];
        this._osMap = {};
        this._basicOSList = [];
        this._vendorMap = {};
        this._exclusionTypes = {
            deviceType: "Device Type",
            hostName: "Hostname",
            ipAddress: "IP Address",
            ipBlock: "IP Block",
            ipRange: "IP Range",
            serialNumber: "Serial Number",
        };
        this._fileFactory = steveIo.fileFactory;
        this._renderer = new Renderer(this, uiUtils);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _triggerDataUpdated() {
        this.emit("DataUpdated");
    }

    _triggerRdDataUpdated() {
        this.emit("RdDataUpdated");
    }

    _triggerAllowedDataUpdated() {
        this.emit("AllowedDataUpdated");
    }

    _triggerExclusionsDataUpdated() {
        this.emit("ExclusionsDataUpdated");
    }

    _registerListeners() {}

    _registerFeedListeners() {
        this._registerCommandSetFeedListeners();

        this._redactionsFeed.on(
            "EntityPropertiesUpdated",
            (this._rdFeedUpdated = this._rdFeedUpdated.bind(this))
        );
        this._redactionsFeed.on(
            "EntityRemoved",
            (this._rdFeedUpdated = this._rdFeedUpdated.bind(this))
        );
        this._redactionsFeed.on(
            "EntityAdded",
            (this._rdFeedUpdated = this._rdFeedUpdated.bind(this))
        );
    }

    _registerCommandSetFeedListeners() {
        this._commandSetsFeed.on(
            "EntityPropertiesUpdated",
            (this._feedUpdated = this._feedUpdated.bind(this))
        );
        this._commandSetsFeed.on(
            "EntityRemoved",
            (this._feedUpdated = this._feedUpdated.bind(this))
        );
        this._commandSetsFeed.on("EntityAdded", (this._feedUpdated = this._feedUpdated.bind(this)));
        this._commandSetsFeed.on(
            "EntityInitialized",
            (this._feedUpdated = this._feedUpdated.bind(this))
        );
        this._commandSetsFeed.on(
            "ChangeFeedInitialized",
            (this._feedUpdated = this._feedUpdated.bind(this))
        );
    }

    _unregisterListeners() {}

    _unregisterCommandSetFeedListeners() {
        this._commandSetsFeed.removeListener("EntityPropertiesUpdated", this._feedUpdated);
        this._commandSetsFeed.removeListener("EntityRemoved", this._feedUpdated);
        this._commandSetsFeed.removeListener("EntityAdded", this._feedUpdated);
        this._commandSetsFeed.removeListener("EntityInitialized", this._feedUpdated);
        this._commandSetsFeed.removeListener("ChangeFeedInitialized", this._feedUpdated);
    }

    _unregisterFeedListeners() {
        this._unregisterCommandSetFeedListeners();
        this._redactionsFeed.removeListener("EntityPropertiesUpdated", this._rdFeedUpdated);
        this._redactionsFeed.removeListener("EntityRemoved", this._rdFeedUpdated);
        this._redactionsFeed.removeListener("EntityAdded", this._rdFeedUpdated);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._unregisterFeedListeners();
        let shutdowns = [
            this._renderer.shutdown(),
            this._commandSetsFeed ? this._commandSetsFeed.shutdown() : Promise.resolve(),
            this._redactionsFeed ? this._redactionsFeed.shutdown() : Promise.resolve(),
        ];
        return Promise.all(shutdowns).then(
            () => {
                this._userSession = undefined;
                this._logger = undefined;
                this._data = undefined;
                this._renderer = undefined;
                this._osMap = undefined;
                this._vendorMap = undefined;
                this._redactionsFeed = undefined;
                this._commandSetsFeed = undefined;
                this._showStandardCommands = undefined;
                this._showDisabledCommands = undefined;
            },
            (err) => {
                console.log("Error shutting down CaptureComponent: ", err);
            }
        );
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get activeOrgName() {
        return this.getActiveOrgName();
    }

    getActiveOrgName() {
        return _.get(this._userSession, "activeOrganization.name");
    }

    isAuthorizedToManageAllowedCommands() {
        return this._authorizedActions.indexOf("manageAllowedCommands") !== -1;
    }

    isAuthorizedToManageExclusions() {
        return this._authorizedActions.indexOf("manageExclusionLists") !== -1;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    whenModelIsReady() {
        return Promise.all([
            this._getAllCommandSets(),
            this._getAllRedactions(),
            this._getAllAllowedCommands(),
            this._getVendorMap(),
            this._getOSMap(),
            this._getOrgStatus(),
            this._getTypeBaseMap(),
            this._getNetworkDiscoveryExclusionEntities(),
        ])
            .then(() => {
                this._registerFeedListeners();
                this._setCommandSetSlickData();
                this._setRedactionSlickData();
                this._setAllowedSlickData();
                this._setExclusionsSlickData();
                return Promise.resolve();
            })
            .catch((e) => {
                console.log("Error", e);
            });
    }

    organizationChanged() {
        return Promise.all([
            this._getAllCommandSets(),
            this._getAllRedactions(),
            this._getAllAllowedCommands(),
            this._getVendorMap(),
            this._getOSMap(),
            this._getOrgStatus(),
            this._getTypeBaseMap(),
            this._getNetworkDiscoveryExclusionEntities(),
        ]).then(() => {
            this._registerFeedListeners();
            this._setCommandSetSlickData();
            this._setRedactionSlickData();
            this._setAllowedSlickData();
            this._setExclusionsSlickData();
            this._triggerDataUpdated();
            this._triggerRdDataUpdated();
            this._triggerAllowedDataUpdated();
            this._triggerExclusionsDataUpdated();
            this.emit("OrgChange");
        });
    }

    _feedUpdated() {
        this._setCommandSetSlickData();
        this._triggerDataUpdated();
    }

    _rdFeedUpdated() {
        this._setRedactionSlickData();
        this._triggerRdDataUpdated();
    }

    _allowedFeedUpdated() {
        this._setAllowedSlickData();
        this._triggerAllowedDataUpdated();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getVendorMap() {
        return this._deviceEntityDef.loadAndMergeVendorMap(this.activeOrgId).then((vendorMap) => {
            this._vendorMap = vendorMap;
            return Promise.resolve();
        });
    }

    _getTypeBaseMap() {
        return this._deviceEntityDef
            .loadAndMergeTypeBaseMap(this.activeOrgId)
            .then((typebaseMap) => {
                this._typeBaseMap = typebaseMap;
                return Promise.resolve();
            });
    }

    _getOSMap() {
        return this._deviceEntityDef.loadAndMergeOsMap(this.activeOrgId).then((osMap) => {
            this._osMap = osMap;
            return Promise.resolve();
        });
    }

    async _getAllCommandSets() {
        const fields = {};

        const showDisabledVal = [true];
        if (this._showDisabledCommands) {
            showDisabledVal.push(false);
        }
        const query = {
            $and: [
                {
                    enabled: {$in: showDisabledVal},
                },
            ],
        };

        if (_.isEmpty(this._commandSetsFeed)) {
            this._commandSetsFeed = this._CommandSetEntityDef.startCommandSetsFeed(
                this.activeOrgId,
                query,
                fields,
                {},
                {skip: 0, limit: MAX_PAGE_SIZE}
            );
        } else {
            this._commandSetsFeed.reestablishFeed(this.activeOrgId, query, fields, {});
            this._feedUpdated();
        }
        return this._commandSetsFeed.whenInitialized();
    }

    _getAllRedactions() {
        if (_.isEmpty(this._redactionsFeed)) {
            this._redactionsFeed = this._redactionRegexEntityDef.startRedactionRegexesFeed(
                this.activeOrgId,
                {},
                {},
                {},
                {skip: 0, limit: MAX_PAGE_SIZE}
            );
        } else {
            this._redactionsFeed.reestablishFeed(this.activeOrgId, {}, {}, {});
        }
        return this._redactionsFeed.whenInitialized();
    }

    async _getAllAllowedCommands() {
        this._allowedCommandEntity = await this._allowedCommandsEntityDef.loadAllowedCommands(
            this.activeOrgId,
            {}
        );
        return Promise.resolve();
    }

    _getOrgStatus() {
        return this._organizationService.getById({id: this.activeOrgId}).then((orgStatus) => {
            this._isHostNamingEnabled = orgStatus.enableHostNaming;
            this._isVerboseLoggingEnabled = orgStatus.verboseCaptureLogging;
            return Promise.resolve();
        });
    }

    async _getNetworkDiscoveryExclusionEntities() {
        this._networkDiscoveryExclusionEntity =
            await this._NetworkDiscoveryExclusionsEntity.loadNetworkDiscoveryExclusions(
                this.activeOrgId
            );
        return Promise.resolve();
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START DATA VALIDATION MEtHODS           */
    /*******************************************/

    async _setEnableHostNaming() {
        const orgInfo = {id: this.activeOrgId, enableHostNaming: this._isHostNamingEnabled};
        const result = await this._organizationService.update(orgInfo);
        return result;
    }

    _setVerboseLogging() {
        const orgInfo = {
            id: this.activeOrgId,
            verboseCaptureLogging: this._isVerboseLoggingEnabled,
        };
        return this._organizationService.update(orgInfo);
    }

    _setCommandSetSlickData() {
        this._basicOSList = [];
        this._slickCommandData = Object.values(this._commandSetsFeed.cache).map((command) => {
            const currentOS = command.getOs();
            if (command.getBasicMode() && command.getEnabled()) {
                if (this._basicOSList.indexOf(currentOS) === -1) {
                    this._basicOSList.push(currentOS);
                }
            }
            return {
                id: command.getId(),
                commandName: command.getName(),
                vendor: command.getVendor(),
                os: this._osMap[command.getOs()] || command.getOs(),
                type: command.getDataType(),
                enabled: command.getEnabled(),
                basicMode: command.getBasicMode() || false,
                deviceFilter: !_.isEmpty(command.getDeviceFilter()),
            };
        });
    }

    _setRedactionSlickData() {
        this._slickRedactionData = Object.values(this._redactionsFeed.cache).map((redaction) => ({
            id: redaction.getId(),
            redactionName: redaction.getName(),
            type: "Regular Expression",
        }));
    }

    _setAllowedSlickData() {
        const group1 = this._allowedCommandEntity.getGroup1() || [];
        const group2 = this._allowedCommandEntity.getGroup2() || [];
        const nonCapture = this._allowedCommandEntity.getNonCapture() || [];

        let slickData = [];

        const loadSlickData = (arr, prop) => {
            for (const element of arr) {
                let found = false;
                const strippedElement = element.replace(/[^\w\s\][,-]/gi, "");

                //find out if already exists in slick array
                found = slickData.find((element) => {
                    if (element.allowedName === strippedElement) {
                        element[prop] = true;
                        return true;
                    }
                    return false;
                });

                if (!found) {
                    const newRow = {
                        id: strippedElement + slickData.length,
                        allowedName: strippedElement,
                        group1: false,
                        group2: false,
                        nonCapture: false,
                    };
                    newRow[prop] = true;
                    slickData.push(newRow);
                }
            }
        };

        loadSlickData(group1, "group1");
        loadSlickData(group2, "group2");
        loadSlickData(nonCapture, "nonCapture");
        this._slickAllowedData = slickData;
    }

    _setExclusionsSlickData() {
        const exclusionsList = _.get(
            this._networkDiscoveryExclusionEntity,
            "_pristineProperties.exclusions",
            []
        );
        const hasPerm = this.isAuthorizedToManageExclusions();
        this._slickExclusionsData = exclusionsList.map((exclusion, idx) => {
            const type = this._findKeyInExclusion(exclusion);
            return {
                id: idx + new Date().getTime(),
                type: this._exclusionTypes[type] || type,
                exType: type,
                value: exclusion[type],
                description: exclusion["description"],
                typeBaseMap: this._typeBaseMap,
                hasPerm,
            };
        });
        return this._slickExclusionsData;
    }

    _findKeyInExclusion(exclusionObj) {
        const keysToFind = Object.keys(this._exclusionTypes);
        const exclusionKeys = Object.keys(exclusionObj);
        const foundKey = keysToFind.find((key) => exclusionKeys.includes(key));

        return foundKey;
    }

    async updateAllowedCommands() {
        // Decode slick data
        let group1 = [];
        let group2 = [];
        let nonCapture = [];
        this._slickAllowedData.forEach((item) => {
            // Strip any special chars from input and then append the necessary regex

            if (!item.allowedName.trim()) {
                this._muf.displayNotification(
                    "A Command cannot be left empty. Please enter a valid Command."
                );
                return false;
            }

            const name = `^${item.allowedName.replace(/[^\w\s\][,-]/gi, "")}.*`;

            if (!item.group1 && !item.group2) {
                if (this._allowedCommandEntity.getGroup1().includes(name)) {
                    item.group1 = true;
                } else {
                    item.group2 = true;
                }

                this._muf.displayNotification(
                    "A command must belong to at least Read Group and/or Write Group."
                );
            }

            if (item.group1) {
                group1.push(name);
            }
            if (item.group2) {
                group2.push(name);
            }
            if (item.nonCapture) {
                nonCapture.push(name);
            }
        });

        // Update the object
        this._allowedCommandEntity.setGroup1(group1);
        this._allowedCommandEntity.setGroup2(group2);
        this._allowedCommandEntity.setNonCapture(nonCapture);
        this._triggerAllowedDataUpdated();
        if (this._allowedCommandEntity.isDirty) {
            try {
                return await this._allowedCommandEntity.update();
            } catch (error) {
                this._logger.warn({error}, "CaptureComponent.updateAllowedCommands() error");
            }
        }
    }

    /*
    resetAllowedCommands() {
        this._muf
            .displayConfirmation(
                "This will reset the commands list to their default values and all other changes will be lost.  Are you sure you want to continue?",
                {
                    title: "Reset to default list",
                    confirmDetails: {
                        text: "Confirm",
                        type: "destructive",
                    },
                    rejectDetails: {
                        text: "Cancel ",
                        type: "text",
                    },
                }
            )
            .then(async () => {
                await this._allowedCommandEntity.restoreDefaultCommands();
                this._setAllowedSlickData();
                this._triggerAllowedDataUpdated();
            });
    }

     */

    deleteItem(targetId) {
        const targetEntity = this._commandSetsFeed.getInstanceById(targetId);
        return targetEntity.delete();
    }

    /*******************************************/
    /* START DATA VALIDATION MEtHODS           */
    /*******************************************/
}

module.exports = CaptureComponent;
CaptureComponent.$groupName = "Organization";
CaptureComponent.$displayName = "Discovery & Capture";
CaptureComponent.$license = ["GLUWARE"];
CaptureComponent.$inject = [
    "$teve.entities.CommandSet",
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.entities.Device",
    "$teve.entities.RedactionRegex",
    "$teve.entities.ConstructInstance",
    "$teve.entities.AllowedCommands",
    "$teve.entities.ValueMap",
    "$teve.services.instance.Organization",
    "$teve.io",
    "$teve.entities.NetworkDiscoveryExclusions",
    "$teve.entities.Site",
    "$teve.entities.CustomFields",
];
